import type {
  CreatedMessage,
  GetMessagePagination,
  GetMessagesByConversationResponse,
  MessagesByConversation,
  NewMessageDto,
} from '@/core/conversations/message/conversation-message.type';
import SearchUtils from '@/utils/search-utils';
import { useAtsConversationApiWithOptions } from '@/composables/useApi';
import type { UserMessageStatus } from './types/get-user-message-status.type';
import { InternalError } from '@/core/shared/errors/internal.error';
import { ErrorName } from '@/core/shared/errors/enums/error-name.enum';
import type { Employer } from '@factoryfixinc/ats-interfaces';
import { USER_OPTED_OUT_ERROR_MESSAGE } from '@/core/sourcing/messaging/shared/constants/message-constants';

export class ConversationMessagePersistence {
  public async fetchConversationMessages(
    conversationId: number,
    page = 1,
    options: { signal?: AbortSignal } = {},
  ): Promise<(MessagesByConversation & { conversationId: number })[]> {
    const { signal } = options;
    if (signal?.aborted) return [];

    const paginationSearch = SearchUtils.convertSearchObjectToSearchUrl({
      pagination: { page } as GetMessagePagination,
    });

    const url = `/message/conversation/${conversationId}/messages?${paginationSearch}`;

    // A deduped GET shares one request, so aborting one attempt would fail the attempt joining it.
    const { execute, abort, data, error, statusCode } = useAtsConversationApiWithOptions({
      dedupe: 'off',
    })(url, { immediate: false })
      .get()
      .json<GetMessagesByConversationResponse>();

    // useFetch replaces any signal passed in fetch options with its own controller.
    signal?.addEventListener('abort', () => abort(), { once: true });
    await execute();

    if (error.value) {
      const internalError = new InternalError('Failed to fetch conversation messages', {
        error: error.value,
        status: statusCode.value,
        data: { conversationId, page },
      });

      if (internalError.name === ErrorName.ABORT_ERROR) {
        return [];
      }

      throw internalError;
    }
    const messages =
      data.value?.messages?.map((m) => ({ ...m, conversationId: conversationId })) ?? [];
    return messages;
  }

  public async getUserMessageStatus(
    userProfileId: number,
    employerId: Employer['id'],
  ): Promise<UserMessageStatus> {
    const url = `message/pro/${userProfileId}/message-status`;
    const { data, error, statusCode } = await useAtsConversationApiWithOptions({
      headers: { 'employer-id': `${employerId}` },
    })(url)
      .get()
      .json<UserMessageStatus>();

    if (error.value) {
      throw new InternalError('Failed to get user message status', {
        error: error.value,
        status: statusCode.value,
        data: { userProfileId },
      });
    }

    if (!data.value) {
      throw new InternalError('Empty response received from getUserMessageStatus', {
        status: statusCode.value,
        data: { userProfileId },
      });
    }

    return data.value;
  }

  /**
   * Sends a message to a conversation
   * @param conversationId - The ID of the conversation
   * @param message - The message to send
   * @returns The created message
   */
  public async sendMessage(
    conversationId: number,
    message: string,
    options?: { jobId?: number },
  ): Promise<CreatedMessage | undefined> {
    const queryParams = new URLSearchParams();
    if (options?.jobId) {
      queryParams.set('jobId', `${options.jobId}`);
    }

    const url = queryParams.size
      ? `/conversation/${conversationId}/message?${queryParams}`
      : `/conversation/${conversationId}/message`;
    const payload: NewMessageDto = {
      message,
    };

    const { data, error } = await useAtsConversationApiWithOptions({ parseError: true })(url)
      .post(payload)
      .json<CreatedMessage>();

    if (error.value || !data.value) {
      if (error.value?.includes('cannot receive messages')) {
        throw new Error(USER_OPTED_OUT_ERROR_MESSAGE);
      }
      throw new Error(error.value || 'Could not send a message');
    }

    return data.value;
  }
}
